import type { FormSubmissionAnswer, FormUploadedAnswerValue } from '~/types/models';

export const FORM_UPLOAD_MAX_BYTES = 25 * 1024 * 1024;
export const FORM_UPLOAD_MAX_FILES_PER_FIELD = 10;

export function isFormUploadedAnswerValue(value: unknown): value is FormUploadedAnswerValue {
    return Boolean(
        value
        && typeof value === 'object'
        && typeof (value as FormUploadedAnswerValue).fileName === 'string'
        && typeof (value as FormUploadedAnswerValue).fileURL === 'string',
    );
}

export function isFormUploadedAnswerValueArray(value: unknown): value is FormUploadedAnswerValue[] {
    return Array.isArray(value) && value.every(isFormUploadedAnswerValue);
}

export function mergeDraftUploadAnswers(
    persistedAnswers: FormSubmissionAnswer[] | null | undefined,
    incomingAnswers: FormSubmissionAnswer[],
): FormSubmissionAnswer[] {
    const persistedByField = new Map(
        (persistedAnswers || []).map(answer => [answer.fieldId, answer]),
    );

    const merged = incomingAnswers.map((answer) => {
        const persisted = persistedByField.get(answer.fieldId);
        if (!isFormUploadedAnswerValueArray(persisted?.value)) return answer;
        if (isFormUploadedAnswerValueArray(answer.value)) return answer;

        return {
            ...answer,
            value: persisted.value,
        };
    });

    for (const persisted of persistedAnswers || []) {
        if (!isFormUploadedAnswerValueArray(persisted.value)) continue;
        if (merged.some(answer => answer.fieldId === persisted.fieldId)) continue;
        merged.push(persisted);
    }

    return merged;
}

export function appendDraftUploadAnswer(
    answers: FormSubmissionAnswer[] | null | undefined,
    fieldId: string,
    fieldLabel: string,
    file: FormUploadedAnswerValue,
): FormSubmissionAnswer[] {
    const next = [...(answers || [])];
    const index = next.findIndex(answer => answer.fieldId === fieldId);
    const currentFiles = index >= 0 && isFormUploadedAnswerValueArray(next[index]?.value)
        ? next[index]!.value
        : [];
    const nextAnswer: FormSubmissionAnswer = {
        fieldId,
        fieldLabel,
        value: [...currentFiles, file],
    };

    if (index >= 0) next[index] = nextAnswer;
    else next.push(nextAnswer);
    return next;
}

export function removeDraftUploadAnswer(
    answers: FormSubmissionAnswer[] | null | undefined,
    fieldId: string,
    fileId: string,
): FormSubmissionAnswer[] {
    return (answers || []).map((answer) => {
        if (answer.fieldId !== fieldId || !isFormUploadedAnswerValueArray(answer.value)) return answer;
        return {
            ...answer,
            value: answer.value.filter(file => file.fileId !== fileId),
        };
    });
}

export function resolvePortalSubmissionAnswers(
    persistedAnswers: FormSubmissionAnswer[] | null | undefined,
    submittedAnswers: FormSubmissionAnswer[],
): FormSubmissionAnswer[] {
    const persistedByField = new Map(
        (persistedAnswers || []).map(answer => [answer.fieldId, answer]),
    );

    const resolved = submittedAnswers.map((answer) => {
        const persisted = persistedByField.get(answer.fieldId);
        if (isFormUploadedAnswerValueArray(persisted?.value)) {
            return { ...answer, value: persisted.value };
        }
        if (isFormUploadedAnswerValueArray(answer.value)) {
            return { ...answer, value: null };
        }
        return answer;
    });

    for (const persisted of persistedAnswers || []) {
        if (!isFormUploadedAnswerValueArray(persisted.value)) continue;
        if (resolved.some(answer => answer.fieldId === persisted.fieldId)) continue;
        resolved.push(persisted);
    }

    return resolved;
}
